import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useFormatCurrency, useCurrency } from '../contexts/CurrencyContext';
import { bffConfigService } from '@resort-os/api-client';
import { useQuery } from '@tanstack/react-query';
import { useModalTour, ModalTourButton } from '../hooks/useModalTour';
import { paymentModalTour, PAYMENT_MODAL_TOUR_KEY } from '../tours/paymentModalTour';

export type PaymentMethod = 'mpesa' | 'cash' | 'card' | 'split';
export type PaymentPhase = 'IDLE' | 'PROCESSING' | 'PENDING' | 'SUCCESS' | 'FAILED';

export interface PaymentReceiptLine {
  label: string;
  value: number;
  highlighted?: boolean;
}

export interface PaymentIntentPayload {
  paymentMethod: PaymentMethod;
  mpesaPhone: string;
  cashReceived: number;
  cardNumber: string;
  tipAmount: number;
  serviceChargeAmount: number;
  splitMpesaAmount: number;
  splitCashAmount: number;
  referenceId: string;
  context: string;
  offlineMode: boolean;
  partialPaymentAmount: number;
}

export interface PaymentSubmissionResult {
  transaction_id?: string;
  reference?: string;
  checkout_request_id?: string;
  message?: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
}

export interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  amount: number;
  referenceId: string;
  context: string;
  currency?: string;
  receiptLines?: PaymentReceiptLine[];
  receiptLabel?: string;
  offlineMode?: boolean;
  allowPartial?: boolean;
  formatCurrency?: (amount: number) => string;
  onSubmit: (payload: PaymentIntentPayload) => Promise<PaymentSubmissionResult | void>;
  onVerify?: (result: PaymentSubmissionResult | null) => Promise<PaymentSubmissionResult | void>;
  onSuccess?: (result: PaymentSubmissionResult | null) => void;
  onFailure?: (error: Error) => void;
}

const BORDER = '1px solid #E2DDD8';
const ACCENT = '#2C3E2D';

const defaultFlags = {
  mpesa: true,
  cash: true,
  card: true,
  split: true,
  tip: true,
  currencyConverter: true,
};

const normalizeAmount = (value: string | number) => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizeStatus = (status?: string): PaymentPhase => {
  const value = (status || '').toLowerCase();
  if (value === 'completed' || value === 'success') return 'SUCCESS';
  if (
    value === 'processing' ||
    value === 'pending' ||
    value === 'pending_approval' ||
    value.includes('approval')
  ) {
    return 'PENDING';
  }
  if (value === 'failed' || value === 'cancelled') return 'FAILED';
  return 'IDLE';
};

export const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  amount,
  referenceId,
  context,
  receiptLines = [],
  receiptLabel = 'Receipt preview',
  offlineMode = false,
  onSubmit,
  onVerify,
  onSuccess,
  onFailure,
  allowPartial = false,
  formatCurrency: formatCurrencyProp,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const formatCurrencyFromContext = useFormatCurrency();
  const formatCurrency = formatCurrencyProp || formatCurrencyFromContext;
  const { currency: defaultCurrency } = useCurrency();

  const defaultCurrencyCode = defaultCurrency?.code || 'KES';

  const fxTargets = useMemo(() => {
    const all = ['USD', 'EUR', 'GBP', 'KES'];
    return all.filter(c => c !== defaultCurrencyCode).slice(0, 3);
  }, [defaultCurrencyCode]);

  const fxSymbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', KES: 'KSh' };

  const { data: configsData } = useQuery({
    queryKey: ['payment-feature-flags'],
    queryFn: () => bffConfigService.getAll(),
    staleTime: 5 * 60 * 1000,
  });

  const configs = configsData?.configs ?? [];
  const getFlag = (key: string): boolean => {
    const c = configs.find((cfg: any) => cfg.key === key);
    if (!c)
      return (defaultFlags as any)[key.replace('feature.', '').replace('_enabled', '')] ?? true;
    if (typeof c.value === 'boolean') return c.value;
    if (typeof c.value === 'string') return c.value.toLowerCase() === 'true';
    return true;
  };

  const flags = {
    mpesa: getFlag('feature.mpesa_enabled'),
    cash: getFlag('feature.cash_enabled'),
    card: getFlag('feature.card_enabled'),
    split: getFlag('feature.split_payments'),
    tip: getFlag('feature.tip_enabled'),
    currencyConverter: getFlag('feature.currency_converter'),
  };

  const { data: fxRates, isLoading: fxLoading } = useQuery({
    queryKey: ['fx-rates', defaultCurrencyCode],
    queryFn: async () => {
      const res = await fetch(
        `https://api.frankfurter.dev/v2/rates?base=${defaultCurrencyCode}&quotes=${fxTargets.join(',')}`,
      );
      if (!res.ok) throw new Error('FX fetch failed');
      const data = await res.json();
      const map: Record<string, number> = {};
      (Array.isArray(data) ? data : []).forEach((item: any) => {
        map[item.quote] = item.rate;
      });
      return map;
    },
    staleTime: 10 * 60 * 1000,
    enabled: flags.currencyConverter,
  });

  const fxRatesMap: Record<string, number> = fxRates ?? {};
  const fxHasData = Object.keys(fxRatesMap).length > 0;

  const staticRates: Record<string, number> = { USD: 0.0077, EUR: 0.0071, GBP: 0.0061 };
  const getFxRate = (target: string): number => {
    if (fxHasData && fxRatesMap[target]) return fxRatesMap[target];
    if (defaultCurrencyCode === 'KES') return staticRates[target] ?? 0;
    if (defaultCurrencyCode === target) return 1;
    return 0;
  };

  const availableMethods: PaymentMethod[] = useMemo(() => {
    const methods: PaymentMethod[] = [];
    if (flags.mpesa) methods.push('mpesa');
    if (flags.cash) methods.push('cash');
    if (flags.card) methods.push('card');
    if (flags.split) methods.push('split');
    return methods;
  }, [flags]);

  const defaultMethod: PaymentMethod = availableMethods[0] || 'cash';

  const [step, setStep] = useState<'details' | 'review' | 'status'>('details');
  const [phase, setPhase] = useState<PaymentPhase>('IDLE');
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentResult, setPaymentResult] = useState<PaymentSubmissionResult | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(defaultMethod);
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [serviceChargeAmount, setServiceChargeAmount] = useState('');
  const [splitMpesaAmount, setSplitMpesaAmount] = useState('');
  const [splitCashAmount, setSplitCashAmount] = useState('');

  const [partialAmount, setPartialAmount] = useState(0);

  const baseAmount = useMemo(() => normalizeAmount(amount), [amount]);
  const tip = useMemo(() => normalizeAmount(tipAmount), [tipAmount]);
  const serviceCharge = useMemo(() => normalizeAmount(serviceChargeAmount), [serviceChargeAmount]);
  const totalDue = useMemo(
    () => baseAmount + tip + serviceCharge,
    [baseAmount, tip, serviceCharge],
  );

  useEffect(() => {
    setPartialAmount(totalDue);
  }, [totalDue]);

  const cash = useMemo(() => normalizeAmount(cashReceived), [cashReceived]);
  const splitMpesa = useMemo(() => normalizeAmount(splitMpesaAmount), [splitMpesaAmount]);
  const splitCash = useMemo(() => normalizeAmount(splitCashAmount), [splitCashAmount]);
  const splitTotal = useMemo(() => splitMpesa + splitCash, [splitMpesa, splitCash]);

  const changeDue = useMemo(() => Math.max(cash - totalDue, 0), [cash, totalDue]);
  const splitBalance = useMemo(() => totalDue - splitTotal, [totalDue, splitTotal]);

  const mpesaValid = mpesaPhone.trim().length > 0;
  const cashValid = cash >= totalDue && totalDue > 0;
  const cardValid = cardNumber.trim().length > 0;
  const splitValid =
    Math.abs(splitBalance) < 0.01 && splitTotal > 0 && (splitMpesa === 0 || mpesaValid);
  const reviewReady =
    paymentMethod === 'mpesa'
      ? mpesaValid
      : paymentMethod === 'cash'
        ? cashValid
        : paymentMethod === 'card'
          ? cardValid
          : splitValid;

  const resetForm = () => {
    setStep('details');
    setPhase('IDLE');
    setErrorMessage('');
    setPaymentResult(null);
    setPaymentMethod(defaultMethod);
    setMpesaPhone('');
    setCashReceived('');
    setCardNumber('');
    setTipAmount('');
    setServiceChargeAmount('');
    setSplitMpesaAmount('');
    setSplitCashAmount('');
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (!flags.split && paymentMethod === 'split') {
      setPaymentMethod(defaultMethod);
    }
    if (!flags.mpesa && paymentMethod === 'mpesa') {
      setPaymentMethod(defaultMethod);
    }
    if (!flags.cash && paymentMethod === 'cash') {
      setPaymentMethod(defaultMethod);
    }
    if (!flags.card && paymentMethod === 'card') {
      setPaymentMethod(defaultMethod);
    }
  }, [paymentMethod, flags, defaultMethod]);

  const buildPayload = (): PaymentIntentPayload => ({
    paymentMethod,
    mpesaPhone: mpesaPhone.trim(),
    cashReceived: cash,
    cardNumber: cardNumber.trim(),
    tipAmount: tip,
    serviceChargeAmount: serviceCharge,
    splitMpesaAmount: splitMpesa,
    splitCashAmount: splitCash,
    referenceId,
    context,
    offlineMode,
    partialPaymentAmount: partialAmount,
  });

  const handleAdvance = () => {
    if (!reviewReady) {
      setErrorMessage(
        paymentMethod === 'mpesa'
          ? 'Enter a valid M-Pesa phone number.'
          : paymentMethod === 'cash'
            ? 'Cash received must cover the amount due.'
            : paymentMethod === 'card'
              ? 'Enter a valid card number.'
              : 'Split amounts must match the amount due.',
      );
      return;
    }

    setErrorMessage('');
    if (step === 'details') {
      setStep('review');
      return;
    }

    void handleSubmit();
  };

  const handleSubmit = async () => {
    setPhase('PROCESSING');
    setErrorMessage('');

    try {
      const result = (await onSubmit(buildPayload())) ?? null;
      setPaymentResult(result);

      const nextPhase = normalizeStatus(result?.status);
      setPhase(nextPhase === 'IDLE' ? 'SUCCESS' : nextPhase);
      setStep('status');

      if (nextPhase === 'PENDING') {
        return;
      }

      onSuccess?.(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Payment failed';
      setPhase('FAILED');
      setStep('status');
      setErrorMessage(message);
      onFailure?.(error instanceof Error ? error : new Error(message));
    }
  };

  const handleVerify = async () => {
    if (!onVerify) return;

    setPhase('PROCESSING');
    setErrorMessage('');

    try {
      const result = (await onVerify(paymentResult)) ?? paymentResult;
      setPaymentResult(result);
      const nextPhase = normalizeStatus(result?.status);
      setPhase(nextPhase === 'IDLE' ? 'PENDING' : nextPhase);

      if (nextPhase === 'SUCCESS') {
        onSuccess?.(result);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to verify payment status';
      setPhase('FAILED');
      setErrorMessage(message);
      onFailure?.(error instanceof Error ? error : new Error(message));
    }
  };

  const renderStatusIcon = () => {
    if (phase === 'SUCCESS') {
      return <CheckCircleIcon sx={{ color: '#2E7D32' }} />;
    }
    if (phase === 'FAILED') {
      return <ErrorIcon sx={{ color: '#C62828' }} />;
    }
    return <PendingActionsIcon sx={{ color: '#ED6C02' }} />;
  };

  const renderStatusCopy = () => {
    if (phase === 'SUCCESS') {
      return paymentResult?.message || 'Payment completed successfully.';
    }

    if (phase === 'PENDING') {
      return paymentResult?.message || 'Payment is pending approval or confirmation.';
    }

    if (phase === 'FAILED') {
      return errorMessage || paymentResult?.message || 'Payment failed.';
    }

    if (phase === 'PROCESSING') {
      return 'Submitting payment to Fiscal Engine...';
    }

    return 'Review the payment details before confirming.';
  };

  const isPending = step === 'status' && phase === 'PENDING';

  const showFormGuide = open && step === 'details' && phase !== 'PROCESSING';
  const { startTour, hasSteps } = useModalTour({
    steps: paymentModalTour,
    tourKey: PAYMENT_MODAL_TOUR_KEY,
    enabled: showFormGuide,
  });

  useEffect(() => {
    if (!isPending) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isPending]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isPending) onClose();
      }}
      disableEscapeKeyDown={isPending}
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 0,
          border: BORDER,
          overflow: 'hidden',
          minWidth: { sm: 480 },
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: BORDER,
          bgcolor: '#FAFAF8',
          px: 3,
          py: 2,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ display: 'block', color: '#8A847C', letterSpacing: '0.16em' }}
            >
              Payment
            </Typography>
            <Typography
              variant="h6"
              title={referenceId}
              sx={{
                color: ACCENT,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 220,
              }}
            >
              {referenceId.length > 12 ? `${referenceId.slice(0, 8)}…` : referenceId}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showFormGuide && hasSteps && <ModalTourButton onStart={startTour} />}
            {!isPending && (
              <IconButton onClick={onClose} size="small" sx={{ color: '#6B6560' }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#FFF' }}>
        {phase === 'PROCESSING' && (
          <LinearProgress
            sx={{ bgcolor: '#EFE8E0', '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }}
          />
        )}

        <Box sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Box
              data-tour="modal-payment-summary"
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: '#8A847C' }}>
                  {context}
                </Typography>
                <Typography variant="h4" sx={{ color: ACCENT, fontWeight: 600, lineHeight: 1.1 }}>
                  {formatCurrency(totalDue)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9B958F' }}>
                  Base amount {formatCurrency(baseAmount)} + adjustments{' '}
                  {formatCurrency(tip + serviceCharge)}
                </Typography>
              </Box>

              <Chip
                icon={renderStatusIcon()}
                label={phase}
                size="small"
                sx={{
                  borderRadius: 0,
                  px: 0.5,

                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  bgcolor:
                    phase === 'SUCCESS'
                      ? '#E8F5E9'
                      : phase === 'FAILED'
                        ? '#FFEBEE'
                        : phase === 'PENDING'
                          ? '#FFF3E0'
                          : '#FAFAF8',
                }}
              />
            </Box>

            {offlineMode && (
              <Alert severity="info" variant="outlined">
                Offline mode enabled. This surface is prepared for queued or fallback payment
                handling.
              </Alert>
            )}

            {step === 'details' && totalDue > 0 && flags.currencyConverter && (
              <Accordion
                disableGutters
                sx={{
                  border: BORDER,
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '& .MuiAccordionSummary-root': { minHeight: 40 },
                  '& .MuiAccordionSummary-content': { margin: '8px 0' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
                  <Typography variant="caption" sx={{ color: '#8A847C' }}>
                    Currency Converter {fxLoading ? '(loading...)' : fxHasData ? '' : '(estimated)'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                  <Stack spacing={0.5}>
                    {fxLoading && (
                      <Typography variant="caption" sx={{ color: '#6B6560', py: 1 }}>
                        Fetching live rates...
                      </Typography>
                    )}
                    {!fxLoading &&
                      fxTargets.map(target => {
                        const rate = getFxRate(target);
                        const converted = totalDue * rate;
                        const symbol = fxSymbols[target] || target;
                        return (
                          <Box
                            key={target}
                            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 1 }}
                          >
                            <Typography variant="caption" sx={{ color: '#6B6560' }}>
                              {target === 'USD'
                                ? 'US Dollar'
                                : target === 'EUR'
                                  ? 'Euro'
                                  : target === 'GBP'
                                    ? 'British Pound'
                                    : 'Kenyan Shilling'}{' '}
                              ({target})
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: ACCENT,

                                fontWeight: 600,
                              }}
                            >
                              {symbol}{' '}
                              {rate > 0
                                ? converted.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                    {!fxHasData && !fxLoading && (
                      <Typography
                        variant="caption"
                        sx={{ color: '#9B958F', fontStyle: 'italic', fontSize: '0.6rem' }}
                      >
                        Offline estimated rates
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {phase !== 'SUCCESS' && phase !== 'FAILED' && (
              <Box sx={{ display: 'grid', gap: 1.5 }} data-tour="modal-payment-fields">
                <Box data-tour="modal-payment-methods">
                  <Typography variant="subtitle2" sx={{ color: ACCENT, fontWeight: 600 }}>
                    Payment Method
                  </Typography>
                  {availableMethods.length === 0 ? (
                    <Alert severity="warning" variant="outlined">
                      No payment methods configured. Enable at least one in the Super Admin Console.
                    </Alert>
                  ) : (
                    <ToggleButtonGroup
                      exclusive
                      value={paymentMethod}
                      onChange={(_, value) => value && setPaymentMethod(value)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${Math.min(availableMethods.length, 3)}, minmax(0, 1fr))`,
                        gap: 1,
                        '& .MuiToggleButton-root': {
                          borderRadius: 0,
                          borderColor: '#D8D1C8',
                          color: '#6B6560',
                          textTransform: 'none',

                          py: 1.1,
                          '&.Mui-selected': {
                            bgcolor: ACCENT,
                            color: '#FFF',
                            borderColor: ACCENT,
                            '&:hover': {
                              bgcolor: '#1B2E22',
                            },
                          },
                        },
                      }}
                    >
                      {flags.mpesa && <ToggleButton value="mpesa">M-Pesa</ToggleButton>}
                      {flags.cash && <ToggleButton value="cash">Cash</ToggleButton>}
                      {flags.card && <ToggleButton value="card">Card</ToggleButton>}
                      {flags.split && <ToggleButton value="split">Split</ToggleButton>}
                    </ToggleButtonGroup>
                  )}
                </Box>

                {flags.tip && (
                  <TextField
                    fullWidth
                    label="Tip / Service charge"
                    type="number"
                    value={tipAmount}
                    onChange={e => setTipAmount(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Optional adjustment included in the preview."
                  />
                )}

                {allowPartial && (
                  <TextField
                    fullWidth
                    label="Amount to pay"
                    type="number"
                    value={partialAmount || ''}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      setPartialAmount(Math.min(val, totalDue));
                    }}
                    inputProps={{ min: 0, step: '0.01', max: totalDue }}
                    helperText={`Max: ${formatCurrency(totalDue)}${partialAmount < totalDue ? ` — Partial payment (balance remaining: ${formatCurrency(totalDue - partialAmount)})` : ''}`}
                  />
                )}

                {(paymentMethod === 'mpesa' || paymentMethod === 'split') && (
                  <TextField
                    fullWidth
                    label={
                      paymentMethod === 'split'
                        ? 'M-Pesa phone number for split'
                        : 'M-Pesa phone number'
                    }
                    placeholder="2547XXXXXXXX"
                    value={mpesaPhone}
                    onChange={e => setMpesaPhone(e.target.value)}
                    helperText={
                      paymentMethod === 'split'
                        ? 'Required when any M-Pesa amount is included.'
                        : undefined
                    }
                  />
                )}

                {paymentMethod === 'cash' && (
                  <TextField
                    fullWidth
                    label="Cash received"
                    type="number"
                    value={cashReceived}
                    onChange={e => setCashReceived(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText={
                      cash > 0
                        ? `Change due: ${formatCurrency(changeDue)}`
                        : 'Enter the amount received.'
                    }
                  />
                )}

                {paymentMethod === 'card' && (
                  <TextField
                    fullWidth
                    label="Card number"
                    placeholder="Enter card number"
                    value={cardNumber}
                    onChange={e => setCardNumber(e.target.value)}
                    helperText="Card payment processed via fiscal engine."
                  />
                )}

                {paymentMethod === 'split' && (
                  <Stack spacing={1.5}>
                    <TextField
                      fullWidth
                      label="M-Pesa portion"
                      type="number"
                      value={splitMpesaAmount}
                      onChange={e => setSplitMpesaAmount(e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                    <TextField
                      fullWidth
                      label="Cash portion"
                      type="number"
                      value={splitCashAmount}
                      onChange={e => setSplitCashAmount(e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }}
                      helperText={
                        splitTotal > 0
                          ? `Balance remaining: ${formatCurrency(Math.max(splitBalance, 0))}`
                          : 'Enter both split amounts.'
                      }
                    />
                  </Stack>
                )}
              </Box>
            )}

            {step !== 'details' && (
              <Paper
                variant="outlined"
                sx={{ borderRadius: 0, borderColor: '#E2DDD8', overflow: 'hidden' }}
              >
                <Box sx={{ p: 2, bgcolor: '#FAFAF8', borderBottom: BORDER }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT }}>
                    {receiptLabel}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#8A847C',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {referenceId.length > 12 ? `${referenceId.slice(0, 8)}…` : referenceId}
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={1.25}>
                    {receiptLines.map(line => (
                      <Box
                        key={line.label}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: line.highlighted ? ACCENT : '#6B6560',
                            fontWeight: line.highlighted ? 600 : 400,
                          }}
                        >
                          {line.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: line.highlighted ? ACCENT : '#1A1A1A',
                            fontWeight: line.highlighted ? 600 : 500,
                          }}
                        >
                          {formatCurrency(line.value)}
                        </Typography>
                      </Box>
                    ))}

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="body2" sx={{ color: '#6B6560' }}>
                        Tip / service charge
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(tip + serviceCharge)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT }}>
                        Amount due
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT }}>
                        {formatCurrency(
                          allowPartial && partialAmount > 0 && partialAmount < totalDue
                            ? partialAmount
                            : totalDue,
                        )}
                      </Typography>
                    </Box>

                    {allowPartial && partialAmount > 0 && partialAmount < totalDue && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption" sx={{ color: '#8A847C' }}>
                          Remaining after this payment
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#8A847C' }}>
                          {formatCurrency(totalDue - partialAmount)}
                        </Typography>
                      </Box>
                    )}

                    {paymentMethod === 'cash' && cash > 0 && (
                      <Alert severity="success" variant="outlined">
                        Cash received: {formatCurrency(cash)}. Change due:{' '}
                        {formatCurrency(changeDue)}.
                      </Alert>
                    )}

                    {paymentMethod === 'split' && splitTotal > 0 && (
                      <Alert severity={splitValid ? 'success' : 'warning'} variant="outlined">
                        Split total: {formatCurrency(splitTotal)}.{' '}
                        {splitValid
                          ? 'Split amounts match the amount due.'
                          : splitMpesa > 0 && !mpesaValid
                            ? 'Enter the M-Pesa phone number to continue.'
                            : `Adjust the split by ${formatCurrency(Math.abs(splitBalance))}.`}
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </Paper>
            )}

            {step === 'status' && (
              <Paper variant="outlined" sx={{ borderRadius: 0, borderColor: '#E2DDD8' }}>
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      {renderStatusIcon()}
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: ACCENT, textTransform: 'capitalize' }}
                      >
                        {phase.toLowerCase()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#6B6560' }}>
                      {renderStatusCopy()}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="body2" sx={{ color: '#6B6560' }}>
                        Amount paid
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(
                          allowPartial && partialAmount > 0 && partialAmount < totalDue
                            ? partialAmount
                            : totalDue - tip - serviceCharge,
                        )}
                      </Typography>
                    </Box>
                    {tip + serviceCharge > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6B6560' }}>
                          Tip / service charge
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(tip + serviceCharge)}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT }}>
                        Total
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT }}>
                        {formatCurrency(totalDue)}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {paymentResult?.reference && (
                      <Typography variant="caption" sx={{ color: '#8A847C' }}>
                        Reference: {paymentResult.reference}
                      </Typography>
                    )}
                    {paymentResult?.checkout_request_id && (
                      <Typography variant="caption" sx={{ color: '#8A847C' }}>
                        Checkout request: {paymentResult.checkout_request_id}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Paper>
            )}

            {step === 'details' && errorMessage && (
              <Alert severity="warning" variant="outlined">
                {errorMessage}
              </Alert>
            )}

            {step === 'review' && errorMessage && (
              <Alert severity="warning" variant="outlined">
                {errorMessage}
              </Alert>
            )}

            {step === 'status' && phase === 'FAILED' && errorMessage && (
              <Alert severity="error" variant="outlined">
                {errorMessage}
              </Alert>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        data-tour="modal-payment-actions"
        sx={{
          borderTop: BORDER,
          bgcolor: '#FAFAF8',
          px: 3,
          py: 2,
          justifyContent: 'space-between',
        }}
      >
        <Button
          disabled={isPending}
          onClick={
            step === 'details' || phase === 'SUCCESS' || phase === 'FAILED'
              ? onClose
              : () => setStep('details')
          }
          sx={{ color: '#6B6560', borderRadius: 0 }}
        >
          {isPending
            ? 'Awaiting Approval'
            : step === 'details' || phase === 'SUCCESS' || phase === 'FAILED'
              ? 'Cancel'
              : 'Back'}
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step === 'status' && phase === 'PENDING' && onVerify && (
            <Button
              variant="outlined"
              onClick={handleVerify}
              sx={{ borderRadius: 0, borderColor: ACCENT, color: ACCENT }}
            >
              Refresh Status
            </Button>
          )}

          {step === 'status' && phase === 'SUCCESS' && (
            <Button variant="contained" onClick={onClose} sx={{ bgcolor: ACCENT, borderRadius: 0 }}>
              Close
            </Button>
          )}

          {step === 'status' && phase === 'FAILED' && (
            <Button
              variant="contained"
              onClick={() => setStep('review')}
              sx={{ bgcolor: ACCENT, borderRadius: 0 }}
            >
              Review Again
            </Button>
          )}

          {step !== 'status' && (
            <Button
              variant="contained"
              onClick={handleAdvance}
              disabled={phase === 'PROCESSING' || (step === 'details' && !reviewReady)}
              sx={{ bgcolor: ACCENT, borderRadius: 0 }}
            >
              {step === 'details' ? 'Review Receipt' : 'Confirm Payment'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
